import React, { useEffect, useState, useContext } from "react";
import { Layout, Menu, Divider, Avatar, Drawer, Button } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { JWTContext } from "../../contexts/jwtContext";
import { useDispatch, useSelector } from "react-redux";
import { clearSelectedBranch } from "../../store/slices/branchesSlice";
import {
  HomeFilled,
  UsergroupAddOutlined,
  GroupOutlined,
  UserOutlined,
  LogoutOutlined,
  FileTextOutlined,
  DollarOutlined,
  BarChartOutlined,
  SwapOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CreditCardOutlined,
  WhatsAppOutlined,
  MenuOutlined,
  EnvironmentOutlined,
  NotificationOutlined,
  TrophyOutlined,
  CalendarOutlined,
  TeamOutlined
} from "@ant-design/icons";
import './index.css'; // CSS dosyasını import edin

export default function MainLayout() {
  let navigate = useNavigate();
  const [current, setCurrent] = useState("Home");
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { Content, Sider, Header } = Layout;
  let location = useLocation();
  const claims = []
  const { logout } = useContext(JWTContext);
  const dispatch = useDispatch();
  const selectedBranch = useSelector((state) => state.branches.selectedBranch);
  const wpInstance = useSelector((state) => state.wpConfig.instance);
  const wpStatusStr = wpInstance?.status?.status || wpInstance?.status || wpInstance?.data?.status;
  const wpConnected = (wpStatusStr === 'connected') || wpInstance?.connected === true || wpInstance?.isConnected === true;
  const {authorities} = useSelector((state) => state.auth);

  // selectedBranch null ise sports ekranına yönlendir
  useEffect(() => {
    if (!selectedBranch && location.pathname !== '/select-sport') {
      navigate('/select-sport');
    }
  }, [selectedBranch, location.pathname, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setCollapsed(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const onClick = (e) => {
    if (e.key === 'logout') {
      logout();
      localStorage.clear();
      navigate('/login');
      return;
    }
    if (e.key === 'changeBranch') {
      dispatch(clearSelectedBranch());
      navigate('/select-sport');
      return;
    }
    setCurrent(e.key);
    navigate(e.key);
    if (isMobile) setDrawerOpen(false);
  };

  const getItem = (label, key, icon, children, options = {}) => {
    return {
      key,
      icon,
      children,
      label,
      ...options,
    };
  };

  const defaultItems = [
    getItem('Ana Sayfa', 'dashboard', <HomeFilled />),
    getItem('Yoklama', 'attendance', <CheckCircleOutlined />),
    getItem('Ödemeler', 'payments', <DollarOutlined />),
    getItem('Öğrenciler', 'students', <UserOutlined />),
    getItem('Maçlar', 'match', <CalendarOutlined />),
    getItem('WP Entegrasyonu', 'wp-root', <WhatsAppOutlined />, [
      getItem('WP Bağla', 'wp', <WhatsAppOutlined />),
      getItem('WP Mesaj Gönder', 'wp-send', <WhatsAppOutlined />),
    ]),
    getItem('Bildirim Gönder', 'notifications', <NotificationOutlined />),
    getItem('Yaş Grupları', 'age-groups', <UsergroupAddOutlined />),
    getItem('Gruplar', 'groups', <GroupOutlined />),
    getItem('Veliler', 'parents', <UserOutlined />),
    getItem('Paketler', 'packages', <GiftOutlined />),
    getItem('Kullanıcılar', 'owners', <TeamOutlined />),
    getItem('Başarılar', 'achievements', <TrophyOutlined />),
    getItem('Haberler', 'news', <FileTextOutlined />),
    getItem('Antrenörler', 'coaches', <UserOutlined />),
    getItem('Ön Kayıtlar', 'pre-registrations', <UserOutlined />),  
    getItem('Şubeler', 'locations', <EnvironmentOutlined />),
    getItem('Raporlar', 'reports', <BarChartOutlined />, [
      {
        key: 'reports-unpaid',
        label: 'Borçlu Öğrenciler',
        icon: <ExclamationCircleOutlined />,
      },
      {
        key: 'attendance-report',
        label: 'Yoklama Raporu',
        icon: <CheckCircleOutlined />,
      },
      {
        key: 'aidat-report',
        label: 'Aidat Raporu',
        icon: <CreditCardOutlined />,
      }
    ]),
    getItem('Finans', 'finance', <DollarOutlined />),
  
  ];

  const trainerItems = [
    getItem('Yoklama', 'attendance', <CheckCircleOutlined />),
  ];

  const items = (authorities || []).includes('Trainer') ? trainerItems : defaultItems;

  const filterHiddenItems = (items, userClaims) => {
    return items
      .map(item => {
        const hasPermission = !item.requiredClaims ||
          item.requiredClaims.some(claim => userClaims.includes(claim));
        if (!hasPermission || item.isHidden) {
          return null;
        }
        return {
          ...item,
          children: item.children
            ? filterHiddenItems(item.children, userClaims)
            : undefined
        };
      })
      .filter(Boolean);
  };
  const visibleItems = filterHiddenItems(items, claims);

  useEffect(() => {
    setCurrent(location.pathname.slice(1));
  }, [location]);

  const getCurrentLabel = () => {
    const currentPath = location.pathname.slice(1);
    const pathSegments = currentPath.split('/');
    const mainKey = pathSegments[0];
    const parent = items.find(x => x.key === mainKey);
    if (parent && !parent.children) {
      return parent.label;
    }
    for (const parent of items) {
      if (parent.children) {
        const child = parent.children.find(x => x.key === mainKey);
        if (child) {
          return child.label;
        }
      }
    }
    return '';
  };

  const wpStatusIcon = (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: wpConnected ? '#52c41a' : '#ff4d4f'
      }}
    />
  );

  return (
    <div>
      <Layout style={{ minHeight: "100vh" }}>
        {/* Mobilde üst bar ve Drawer */}
        {isMobile && (
          <Header style={{ background: '#fff', padding: '0 16px', display: 'flex', alignItems: 'center', height: 56, boxShadow: '0 2px 8px #f0f1f2' }}>
            <Button type="text" icon={<MenuOutlined style={{ fontSize: 24 }} />} onClick={() => setDrawerOpen(true)} />
            <div style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 18 }}>{getCurrentLabel()}</div>
          </Header>
        )}
        {/* Masaüstünde klasik Sider */}
        {!isMobile && (
          <Sider
            theme="light"
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            width={250}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ alignContent: "center", justifyContent: "center", display: "flex" }}>
                <Avatar
                  size={collapsed ? 80 : 200}
                  src={require("../../assets/logo.png")}
                  style={{ width: collapsed ? 80 : 200, height: collapsed ? 80 : 200 }}
                />
              </div>
              <Menu
                theme="light"
                onClick={onClick}
                selectedKeys={[current]}
                mode="inline"
                items={visibleItems}
                style={{ marginTop: 5, flex: 1 }}
              />
              <div style={{ marginTop: 'auto' }}>
                <Divider style={{ margin: '8px 0' }} />
                <Menu
                  theme="light"
                  onClick={onClick}
                  mode="inline"
                  style={{ borderTop: 'none' }}
                  items={[
                    getItem(wpConnected ? 'WP Bağlı' : 'WP Bağlı Değil', 'wp', wpStatusIcon),
                    getItem('Branş Değiştir', 'changeBranch', <SwapOutlined style={{ color: '#1890ff' }} />),
                    getItem('Çıkış Yap', 'logout', <LogoutOutlined style={{ color: '#ff4d4f' }} />)
                  ]}
                />
              </div>
            </div>
          </Sider>
        )}
        {/* Mobilde Drawer ile menü */}
        {isMobile && (
          <Drawer
            title={<Avatar size={80} src={require("../../assets/logo.png")} style={{ margin: '0 auto', display: 'block' }} />}
            placement="left"
            closable={true}
            onClose={() => setDrawerOpen(false)}
            open={drawerOpen}
            bodyStyle={{ padding: 0 }}
            width={250}
          >
            <Menu
              theme="light"
              onClick={onClick}
              selectedKeys={[current]}
              mode="inline"
              items={visibleItems}
              style={{ marginTop: 5, flex: 1, border: 'none' }}
            />
            <div style={{ marginTop: 'auto' }}>
              <Divider style={{ margin: '8px 0' }} />
              <Menu
                theme="light"
                onClick={(e) => { onClick(e); setDrawerOpen(false); }}
                mode="inline"
                style={{ borderTop: 'none' }}
                items={[
                  getItem(wpConnected ? 'WP Bağlı' : 'WP Bağlı Değil', 'wp', wpStatusIcon),
                  getItem('Branş Değiştir', 'changeBranch', <SwapOutlined style={{ color: '#1890ff' }} />),
                  getItem('Çıkış Yap', 'logout', <LogoutOutlined style={{ color: '#ff4d4f' }} />)
                ]}
              />
            </div>
          </Drawer>
        )}
        <Layout className="site-layout">
          <Content style={{ margin: isMobile ? 0 : "0 16px" }}>
            <div
              className="site-layout-background"
              style={{ padding: isMobile ? 0 : 24, minHeight: 360 }}
            >
              {!isMobile && <Divider>{getCurrentLabel()}</Divider>}
              <Outlet />
            </div>
          </Content>
        </Layout>
      </Layout>
    </div>
  );
}
